import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DollarSign, ChevronRight } from "lucide-react";
import { NavLink } from "react-router-dom";

/** Purple banner listing MRs stuck in `pending_approval` — visible to PM, Analyst, Manager, Admin.
 *  Refreshes every 60s. Clicking the item takes the user to the MR list. */
const REFRESH_MS = 60_000;
const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

export default function QuoteApprovalsWidget() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/material-requests?status=pending_approval");
      // For PM: show only MRs where they're the assigned PM
      const filtered = user.role === "pm"
        ? r.filter((mr) => !mr.pm_id || mr.pm_id === user.id)
        : r;
      setRows(filtered);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [user.role, user.id]);

  useEffect(() => {
    if (!["pm", "analista", "admin", "manager"].includes(user.role)) return;
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load, user.role]);

  if (!["pm", "analista", "admin", "manager"].includes(user.role)) return null;
  if (loading || rows.length === 0) return null;

  return (
    <div data-testid="quote-approvals-widget" className="bg-purple-50 border-l-4 border-purple-500 rounded-lg overflow-hidden mb-6">
      <div className="px-4 py-3 flex items-center gap-2 border-b border-purple-200">
        <DollarSign className="w-4 h-4 text-purple-700" />
        <span className="text-sm font-bold text-purple-900">Quote approvals awaiting your decision ({rows.length})</span>
      </div>
      <ul className="divide-y divide-purple-200">
        {rows.slice(0, 5).map((mr) => {
          const quotes = mr.quotes || [];
          const cheapest = quotes.length ? quotes.reduce((a, b) => (a.total < b.total ? a : b)) : null;
          return (
            <li key={mr.id} data-testid={`quote-approval-${mr.id}`} className="px-4 py-2.5 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-800 truncate">
                  <span className="font-semibold">{mr.number}</span>{" "}
                  <span className="text-slate-500">— {mr.project_name || mr.ticket_title || "material request"}</span>
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {quotes.length} quote{quotes.length !== 1 ? "s" : ""}
                  {cheapest && <> · cheapest <b>{cheapest.supplier_name}</b> {money(cheapest.total)}</>}
                  {" · "}{mr.updated_at?.slice(0, 10)}
                </div>
              </div>
              <NavLink
                to="/material-requests"
                data-testid={`quote-approval-open-${mr.id}`}
                className="inline-flex items-center gap-1 text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white rounded-lg px-2.5 py-1.5"
              >
                Review <ChevronRight className="w-3.5 h-3.5" />
              </NavLink>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
